/**
 * THE STANDINGDOCS OPERATIONS RUNBOOK, IN THE PLATFORM CONSOLE.
 *
 * WHO THIS IS FOR. A StandingDocs PLATFORM system administrator: the people
 * who operate the whole service across every customer. It is emphatically NOT
 * for a customer's own System Admin, who is the highest administrator inside
 * ONE organization and holds the `sysadmin` membership role there. Those two
 * are different authorities with a confusable name, which is why the page says
 * "platform operations" in its own words rather than "system admin".
 *
 * HOW IT IS PROTECTED. It is a child of `/console`, and `ConsoleShell` already
 * refuses anybody without `session.platformSysAdmin` and then asks for the
 * second factor. Nothing new was invented for this page: a route added here is
 * protected because of where it lives, which is the point of having one gate.
 * The navigation item lives inside the same shell, so it cannot appear to
 * somebody the route would refuse, and typing the URL meets the same gate.
 *
 * WHY THERE IS NO PROSE IN THIS FILE. The documents are read from the
 * repository at build time (`runbookDocs.ts`). A page that restated them would
 * be wrong within a month, and a wrong runbook is worse than none, because
 * somebody follows it during an incident.
 *
 * READ ONLY, DELIBERATELY. The checklists render as checklists because that is
 * what they are, and the boxes do not take a click. A box that looked
 * checkable and remembered nothing would be worse than a box that plainly does
 * not, and a box that DID remember would be operational state this version has
 * no business holding.
 */

import { useMemo } from 'react';
import { PageState } from '../../shared/PageState';
import { OperationsTabs } from './OperationsTabs';
import { parseMarkdown, tableOfContents, type Block, type ListItem, type Span } from './markdown';
import { DEFAULT_DOC_ID, docById } from './runbookDocs';
import page from '../../shared/Page.module.css';
import own from './Runbook.module.css';

function Inline({ spans }: { spans: Span[] }) {
  return (
    <>
      {spans.map((s, i) => {
        if (s.code) return <code key={i}>{s.text}</code>;
        if (s.href) {
          const external = /^https?:/i.test(s.href);
          return (
            <a
              key={i}
              href={s.href}
              {...(external ? { target: '_blank', rel: 'noreferrer noopener' } : {})}
            >
              {s.text}
            </a>
          );
        }
        if (s.strong) return <strong key={i}>{s.text}</strong>;
        return <span key={i}>{s.text}</span>;
      })}
    </>
  );
}

function Task({ item }: { item: ListItem }) {
  return (
    <li className={own.task}>
      {/* A drawn box, never a form control, so nothing invites a click that
          would be forgotten the moment the page reloads. */}
      <span className={own.box} aria-hidden="true" />
      <span className={own.taskBody}>
        <Inline spans={item.spans} />
        {item.blocks.length > 0 && <Blocks blocks={item.blocks} />}
      </span>
    </li>
  );
}

function Blocks({ blocks }: { blocks: Block[] }) {
  return (
    <>
      {blocks.map((block, i) => {
        switch (block.kind) {
          case 'heading': {
            const Tag = (block.level <= 2 ? 'h2' : block.level === 3 ? 'h3' : 'h4') as 'h2' | 'h3' | 'h4';
            return <Tag key={i} id={block.id}>{block.text}</Tag>;
          }
          case 'paragraph':
            return <p key={i}><Inline spans={block.spans} /></p>;
          case 'code':
            return <pre key={i} className={own.code}><code>{block.text}</code></pre>;
          case 'rule':
            return <hr key={i} />;
          case 'callout':
            return (
              <div key={i} className={own.callout} role="note">
                <Blocks blocks={block.blocks} />
              </div>
            );
          case 'list': {
            const tasks = block.items.some((it) => it.checked !== undefined);
            if (tasks) {
              return (
                <ul key={i} className={own.tasks}>
                  {block.items.map((item, n) => <Task key={n} item={item} />)}
                </ul>
              );
            }
            const Tag = block.ordered ? 'ol' : 'ul';
            return (
              <Tag key={i} className={own.list}>
                {block.items.map((item, n) => (
                  <li key={n}>
                    <Inline spans={item.spans} />
                    {item.blocks.length > 0 && <Blocks blocks={item.blocks} />}
                  </li>
                ))}
              </Tag>
            );
          }
          case 'table':
            return (
              <div key={i} className={own.tableWrap}>
                <table className={own.table}>
                  <thead>
                    <tr>{block.head.map((cell, n) => <th key={n} scope="col"><Inline spans={cell} /></th>)}</tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row, n) => (
                      <tr key={n}>{row.map((cell, c) => <td key={c}><Inline spans={cell} /></td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          default:
            return null;
        }
      })}
    </>
  );
}

/**
 * The document is a PROP, not a URL parameter, and the route table names both
 * pages explicitly.
 *
 * These are two fixed pages, not a detail view of a list, and writing them as
 * `operations/:docId` made them look like one: `detailPages.guard.test.ts`
 * reads the route table and asks every parameterised route for a back arrow,
 * correctly, because a detail page reached from a list needs a way back to it.
 * There is no list here. The way between the two documents is the sub-
 * navigation, which is on the page and always visible.
 */
export function RunbookPage({ docId = DEFAULT_DOC_ID }: { docId?: string }) {
  const doc = docById(docId);

  const parsed = useMemo(() => (doc ? parseMarkdown(doc.source) : []), [doc]);
  const contents = useMemo(() => tableOfContents(parsed), [parsed]);

  return (
    <>
      <header className={page.header}>
        <h1 className={page.title}>StandingDocs Operations Runbook</h1>
        <p className={page.subtitle}>
          Internal platform operations documentation, for the people who run StandingDocs across
          every customer. Not customer documentation, and not an organization&rsquo;s own settings.
          It is read from the repository, so what is written here is what is in the code.
        </p>
      </header>

      <OperationsTabs />

      {!doc ? (
        <PageState title="No such runbook" tone="alert" action={{ to: `/console/operations/${DEFAULT_DOC_ID}`, label: 'Go-live runbook' }}>
          There is no operational document with that address.
        </PageState>
      ) : (
        <>
          <p className={own.tabBlurb}>{doc.blurb}</p>
          <div className={own.layout}>
            <nav className={own.contents} aria-label="Contents">
              <p className={own.contentsTitle}>Contents</p>
              <ul className={own.contentsList}>
                {contents.map((entry) => (
                  <li key={entry.id} className={entry.level === 3 ? own.nested : undefined}>
                    <a href={`#${entry.id}`}>{entry.text}</a>
                  </li>
                ))}
              </ul>
            </nav>

            <article className={own.doc}>
              <Blocks blocks={parsed} />
              <p className={own.source}>
                Source: <code>docs/{doc.id === 'runbook' ? 'PILOT-GO-LIVE' : 'PILOT-DISASTER-RECOVERY'}.md</code>.
                Edit the file in the repository; this page follows at the next build.
              </p>
            </article>
          </div>
        </>
      )}
    </>
  );
}
